import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
import { useTrialContext } from '../hooks/useTrialContext';

interface ReportRow {
  id: string;
  report_type: 'product_discovery' | 'lead_generation';
  week_start: string;
  published_at: string | null;
}

function formatWeek(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${start.getFullYear()}`;
}

const TYPE_LABEL: Record<string, string> = {
  product_discovery: '🔬 Product Discovery',
  lead_generation: '🎯 Lead Generation',
};

const TYPE_COLOR: Record<string, string> = {
  product_discovery: 'bg-blue-50 text-blue-700 border-blue-200',
  lead_generation: 'bg-green-50 text-green-700 border-green-200',
};

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const trial = useTrialContext();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userData?.company_id) {
        setLoading(false);
        return;
      }

      const { data } = await supabase
        .from('customer_reports')
        .select('id, report_type, week_start, published_at')
        .eq('company_id', userData.company_id)
        .eq('status', 'published')
        .order('week_start', { ascending: false });

      setReports(data ?? []);
      setLoading(false);
    };
    load();
  }, []);

  const grouped = reports.reduce<Record<string, ReportRow[]>>((acc, r) => {
    acc[r.week_start] = acc[r.week_start] ?? [];
    acc[r.week_start].push(r);
    return acc;
  }, {});

  const weekKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="relative max-w-3xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Intelligence Reports</h1>
        <p className="text-sm text-gray-500 mt-1">
          Weekly AI-generated insights from your LinkedIn outreach campaigns, reviewed by your CSR.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-gray-500">Loading reports...</div>
      ) : weekKeys.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
          <div className="text-3xl mb-3">📊</div>
          <p className="text-sm font-medium text-gray-600">No reports published yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Your first report will appear here after your CSR reviews and publishes it.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {weekKeys.map(week => (
            <div key={week}>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Week of {formatWeek(week)}
              </h2>
              <div className="space-y-3">
                {grouped[week].map(report => (
                  <button
                    key={report.id}
                    onClick={() => navigate(`/reports/${report.id}`)}
                    className="w-full text-left bg-white border border-gray-200 rounded-xl px-6 py-4 hover:border-purple-300 hover:shadow-sm transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${TYPE_COLOR[report.report_type]}`}
                      >
                        {TYPE_LABEL[report.report_type]}
                      </span>
                      <span className="text-purple-600 text-sm font-medium group-hover:underline">
                        View Report →
                      </span>
                    </div>
                    {report.published_at && (
                      <p className="text-xs text-gray-400 mt-2">
                        Published{' '}
                        {new Date(report.published_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grey overlay for free users (signed up but not subscribed) */}
      {!trial.isLoading && (trial.isFreeUser || trial.isPaidLapsed) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded-xl"></div>
          <div className="relative bg-white p-8 rounded-xl shadow-2xl max-w-md mx-4 text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-blue-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {trial.isPaidLapsed ? 'Subscription Expired' : 'Activate your account to access Intelligence Reports'}
            </h3>
            <p className="text-gray-600 mb-6">
              {trial.isPaidLapsed
                ? 'Your subscription has lapsed. Renew to keep accessing your weekly AI-generated intelligence reports.'
                : 'Get dashboard access for $30/month to unlock your weekly AI-generated intelligence reports.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/pricing')}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                {trial.isPaidLapsed ? 'Renew — $30/month' : 'Get Dashboard Access — $30/month'}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
